import React, { useEffect } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { BillingRow } from "../../../../types";
import { useDownloadRazorpayInvoiceMutation } from "../../../../services/subscription/subscriptionLicense.rtk.api";

type InvoiceModalProps = {
  open: boolean;
  data: BillingRow;
  onClose: () => void;
};
const currencySymbols: Record<string, string> = {
  INR: "₹",
  AED: "د.إ",
  USD: "$",
  // ...add more as needed
};
const InvoiceModal: React.FC<InvoiceModalProps> = ({ open, data, onClose }) => {
  const { theme } = useTheme();
  const [downloadRazorpayInvoice, { isLoading: isDownloading }] =
    useDownloadRazorpayInvoiceMutation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const panelBg =
    theme === "dark"
      ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
      : "bg-white text-gray-900 border-gray-200";

  // Precise colors to match screenshot
  const dividerColor = theme === "dark" ? "#2F2F2F" : "#E5E7EB"; // inner separators
  const boxBg = theme === "dark" ? "#161616" : "#F9FAFB"; // details box bg
  const boxBorder = theme === "dark" ? "#343434" : "#E5E7EB"; // details box border

  const softCard =
    theme === "dark"
      ? "bg-[#252525] border border-[#2C2C2C] text-gray-200"
      : "bg-gray-50 border border-gray-200 text-gray-800";

  const labelCls = "text-xs text-gray-400";
  const valCls =
    "text-sm font-medium " +
    (theme === "dark" ? "text-gray-200" : "text-gray-800");

  const statusStyles =
    data.status === "Paid"
      ? "bg-[#1f2a1f] text-[#C6D92C] border border-[#2a3a2a]"
      : data.status === "Overdue"
        ? "bg-[#2a1f1f] text-[#ff8a80] border border-[#3a2a2a]"
        : "bg-[#2a2a1f] text-[#E6CF57] border border-[#3a3a2a]";

  // Use API data for invoice details
  const invoiceNo = data.code || "-";
  const paymentMethod = "MASTERCARD 0078"; // Replace with actual if available
  const taxes = data.taxAmount ?? 0;
  const subTotal = (data.totalProductAmount ?? data.total) - taxes;
  const currencySymbol = currencySymbols[data.currency] || data.currency || "₹";

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Right Drawer */}
      <aside
        className={`fixed right-0 top-0 h-full w-full sm:w-[420px] md:w-[420px] z-50 ${panelBg} border-l`}
        role="dialog"
        aria-modal="true"
        style={{ borderLeftColor: dividerColor }}
      >
        {/* Local scrollbar styling for modal content */}
        <style>{`
          .invoice-modal-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #000 transparent;
          }
          .invoice-modal-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .invoice-modal-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .invoice-modal-scrollbar::-webkit-scrollbar-thumb {
            background-color: #000;
            border-radius: 8px;
          }
        `}</style>

        <div
          className={`flex items-center justify-between px-3 py-2 border-b`}
          style={{ borderColor: dividerColor }}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Invoice</h2>
          </div>
          <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyles}`}>
            {data.status}
          </span>
        </div>

        <div className="h-[calc(100%-56px)] overflow-y-auto px-2 py-4 space-y-4 invoice-modal-scrollbar">
          {/* Details Box (boxy effect with exact borders) */}
          <div
            className="rounded-lg p-3 border"
            style={{ backgroundColor: boxBg, borderColor: boxBorder }}
          >
            <div className="grid grid-cols-1">
              <div
                className="pb-2 border-b border-dashed"
                style={{ borderColor: dividerColor }}
              >
                <div className={labelCls}>End Date</div>
                <div className={valCls}>{data.endDate}</div>
              </div>
              <div
                className="py-2 border-b border-dashed"
                style={{ borderColor: dividerColor }}
              >
                <div className={labelCls}>Invoice Number</div>
                <div className={valCls}>{invoiceNo}</div>
              </div>
              <div className="pt-2">
                <div className={labelCls}>Payment Method</div>
                <div className={valCls}>{paymentMethod}</div>
              </div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Cost Details</h3>

            <div className={`${softCard} rounded-lg`}>
              <div className="p-3">
                <div className="grid grid-cols-3 text-xs text-gray-400">
                  <div>Type</div>
                  <div className="text-center">Count</div>
                  <div className="text-right">Cost ₹</div>
                </div>
                <div
                  className="mt-2 border-t"
                  style={{ borderColor: dividerColor }}
                />

                <div className="mt-2 space-y-2 text-sm">
                  <div className="grid grid-cols-3 items-center">
                    <div>Premium</div>
                    <div className="text-center">
                      {data.premium ? data.premium.quantity : 0}
                    </div>
                    <div className="text-right">
                      {currencySymbol}
                      {data.premium
                        ? data.premium.totalPrice?.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 items-center">
                    <div>Basic</div>
                    <div className="text-center">
                      {data.normal ? data.normal.quantity : 0}
                    </div>
                    <div className="text-right">
                      {currencySymbol}
                      {data.normal
                        ? data.normal.totalPrice?.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 items-center">
                    <div>Premium Addons</div>
                    <div className="text-center">
                      {data.premiumAddons ? data.premiumAddons.quantity : 0}
                    </div>
                    <div className="text-right">
                      {currencySymbol}
                      {data.premiumAddons
                        ? data.premiumAddons.totalPrice?.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 items-center">
                    <div>Basic Addons</div>
                    <div className="text-center">
                      {data.basicAddons ? data.basicAddons.quantity : 0}
                    </div>
                    <div className="text-right">
                      {currencySymbol}
                      {data.basicAddons
                        ? data.basicAddons.totalPrice?.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="px-4 py-3 border-t space-y-2 text-sm"
                style={{ borderColor: dividerColor }}
              >
                <div className="grid grid-cols-2">
                  <div className="text-gray-400">Sub Total</div>
                  <div className="text-right">
                    {currencySymbol}
                    {subTotal?.toFixed(2)}
                  </div>
                </div>
                {data.isCouponApplied && (
                  <div className="grid grid-cols-2">
                    <div
                      className="text-gray-400"
                      style={{ width: "max-content" }}
                    >
                      Discount
                      {data.coupon
                        ? ` (${data.coupon.code} - ${data.coupon.discountType === "PERCENTAGE"
                          ? `${data.coupon.discountPercent}%`
                          : `${currencySymbol}${data.coupon.discountAmount}`
                        })`
                        : ""}
                    </div>
                    <div className="text-right">
                      -{currencySymbol}
                      {data.totalReductionAmount?.toFixed(2)}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2">
                  <div className="text-gray-400">Tax</div>
                  <div className="text-right">
                    {currencySymbol}
                    {taxes?.toFixed(2)}
                  </div>
                </div>
                <div className="grid grid-cols-2 pt-2">
                  <div className="font-semibold">Total</div>
                  <div className="text-right font-semibold">
                    {currencySymbol}
                    {(data.totalAmount ?? data.total)?.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {data.status === "Paid" && (() => {
              const isRazorpay = !!data?.gateway?.razorpay;



              return (
                <div className="space-y-4 pt-6 mx-auto w-56 sm:w-64">
                  {/* Razorpay badge */}


                  {/* Download PDF — only for Stripe (invoiceUrl exists) */}
                  {isRazorpay ? (
                    // RAZORPAY BUTTON
                    <button
                      type="button"
                      // CHANGE THIS: Add your Razorpay-specific download function or URL here
                      onClick={() => {
                        downloadRazorpayInvoice({ invoiceId: data.id })
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-[#B6CB24] text-[#1A1A1A] font-medium hover:bg-[#A8BD1F] transition-colors"
                    >
                      {/* Add Razorpay Icon here if you want it to look different */}
                      Download  PDF
                    </button>
                  ) : (
                    // STRIPE BUTTON (Default)
                    <button
                      type="button"
                      onClick={() => window.open(data.invoiceUrl, "_blank", "noopener,noreferrer")}
                      className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-[#B6CB24] text-[#1A1A1A] font-medium hover:bg-[#A8BD1F] transition-colors"
                    >
                      {/* Your existing PDF icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                        <path d="..." fill="#F3F3F6" />
                        <path d="..." fill="#F3F3F6" />
                      </svg>
                      Download PDF
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={onClose}
                    className={`w-full h-10 rounded-lg font-medium border ${theme === "dark"
                      ? "bg-[#2A2A2A] border-[#3A3A3A] text-gray-200 hover:bg-[#333333]"
                      : "text-[#1A1A1A] bg-[#EDF5C0] border-[#B6CB24]"
                      }`}
                  >
                    Close
                  </button>
                </div>
              );
            })()}

          </div>
        </div>
      </aside>
    </>
  );
};

export default InvoiceModal;
